import React, { useState } from 'react';
import {
  Sparkles,
  ShieldCheck,
  Award,
  Clock,
  MapPin,
  ChevronRight,
  Heart,
  Droplets,
  Zap,
  CheckCircle2,
  Calendar,
  MessageCircle,
  ArrowUpRight,
  UserCheck,
  Star,
  Users,
  Compass,
  Syringe,
  Smile,
  Flame,
  Activity,
  Check,
  Stethoscope,
  BadgeCheck,
  PhoneCall,
  Sparkle
} from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { BlogPost } from '../types';
import { isProductionDomain } from '../utils/siteConfig';
import { BlogSection } from './BlogSection';
import { FaqSection } from './FaqSection';
import { LocationSection } from './LocationSection';

interface ApollonLandingProps {
  onOpenLeadModal: (title?: string) => void;
  posts: BlogPost[];
  isBlogsLoading: boolean;
  onNavigateToPost: (slug: string) => void;
}

export const ApollonLanding: React.FC<ApollonLandingProps> = ({
  onOpenLeadModal,
  posts,
  isBlogsLoading,
  onNavigateToPost,
}) => {
  const { setSiteKey, siteProfile } = useSite();
  const [selectedService, setSelectedService] = useState<string>('hydrafacial');

  const aestheticServices = [
    {
      id: 'hydrafacial',
      title: 'Hydrafacial Medikal Cilt Bakımı',
      tagline: 'Vakumlu Derin Gözenek Temizliği & Yoğun Hyaluronik Nem',
      duration: '45-60 Dk',
      benefit: 'İlk seanstan itibaren parlak, siyah noktasız ve neme doymuş taze bir cilt',
      icon: Droplets,
      targetLayer: 'Epidermis & Gözenek Kanalı',
      longevity: '3-4 Hafta Canlılık',
      recoveryTime: 'Sıfır (Anında Işıltı)',
      doctorLevel: 'Medikal Estetisyen & Hekim Protokolü',
      stages: [
        { step: '01', name: 'Vorteks Temizleme', desc: 'Vakumlu solüsyonla ölü deri ve keratin tabakanın nazikçe soyulması' },
        { step: '02', name: 'Derin Gözenek Detoksu', desc: 'Salisilik asit ile siyah nokta, komedon ve fazla sebumun tahliyesi' },
        { step: '03', name: 'Hyaluronik & Peptid İnfüzyon', desc: 'Antioksidan serumların cilt altına basınçla yedirilmesi' },
        { step: '04', name: 'LED Terapi & Yatıştırma', desc: 'Kolajen uyarıcı ışık terapisi ve koruyucu bariyer kilidi' }
      ],
      highlights: [
        'Vorteks teknolojisi ile acısız derin gözenek ve siyah nokta arındırma',
        'Cilt tipine özel antioksidan, peptid ve hyaluronik asit infüzyonu',
        'Gözenek sıkılaştırma, sebum dengesi ve renk tonu eşitleme',
        'Her cilt tipine ve dört mevsime uygun konforlu medikal protokol'
      ]
    },
    {
      id: 'botox',
      title: 'Medikal Botoks & Kırışıklık Tedavisi',
      tagline: 'Alın, Kaş Ortası, Göz Çevresi & Masseter Botoksu',
      duration: '15-20 Dk',
      benefit: 'Doğal mimikleri dondurmadan kırışıklıkları pürüzsüzleştiren hekim uygulaması',
      icon: Sparkles,
      targetLayer: 'Hedef Yüz Kas Grupları (SMAS Üstü)',
      longevity: '4 - 6 Ay Etki',
      recoveryTime: 'Sosyal Hayata Anında Dönüş',
      doctorLevel: 'Yalnızca Uzman Hekim Tarafından',
      stages: [
        { step: '01', name: 'Mimik & Doku Haritalama', desc: 'Dinamik kırışıklık hatlarının ve asimetrilerin hekim tarafından incelenmesi' },
        { step: '02', name: 'Mikro Doz Planlama', desc: 'Doğal ifadeyi koruyacak ünite ve enjeksiyon noktalarının belirlenmesi' },
        { step: '03', name: 'Ağrısız Mikro Enjeksiyon', desc: 'İnce uçlu insülin iğneleri ve anestezik kremle konforlu uygulama' },
        { step: '04', name: '10-14 Gün Sonra Kontrol', desc: 'Sonuçların oturması ve gerekirse ücretsiz rötuş değerlendirmesi' }
      ],
      highlights: [
        'Alın çizgileri, kaz ayakları ve kaş arası kırışıklıklarında etkili açılma',
        'Masseter (çene) botoksu ile diş sıkma (bruksizm) tedavisi & V-line yüz incelmesi',
        'Aşırı terleme tedavisi (koltuk altı botoksu) ile 6-8 ay tam kuruluk',
        'FDA onaylı orijinal ürünler ve uzman hekim kontrolünde güvenli uygulama'
      ]
    },
    {
      id: 'dermal-fillers',
      title: 'Dudak & Yüz Dolgusu Uygulamaları',
      tagline: 'Doğal Dudak Hacmi, Çene (Jawline), Nazolabial & Elmacık Dolgusu',
      duration: '25-35 Dk',
      benefit: 'Kişinin yüz oranlarına uygun, simetrik, estetik ve doğal hacim kazandırma',
      icon: Smile,
      targetLayer: 'Subdermal Doku & Derin Dermis',
      longevity: '9 - 18 Ay Kalıcılık',
      recoveryTime: '1-2 Gün Minimal Hassasiyet',
      doctorLevel: 'Yalnızca Uzman Hekim Tarafından',
      stages: [
        { step: '01', name: 'Altın Oran Yüz Analizi', desc: 'Profiloplasti, çene hattı ve dudak simetrisinin belirlenmesi' },
        { step: '02', name: 'Lokal Anestezik Hazırlık', desc: 'Bölgesel uyuşturma ile tamamen acısız ve konforlu işlem süreci' },
        { step: '03', name: 'Kanül / İğne ile Şekillendirme', desc: 'Saf hyaluronik asit ile damar güvenliği gözetilerek doğal dolgu' },
        { step: '04', name: 'Şekil Verme & Doku Masajı', desc: 'Topaklanma yapmayan homojen dağılım ve anında gözlemlenen sonuç' }
      ],
      highlights: [
        'Russian lips veya doğal hatlı hyaluronik asit dudak dolgusu',
        'Çene hattını belirginleştiren ve yüz ovalini toparlayan Jawline dolgusu',
        'Burun-ağız kenarı (nazolabial) çöküntülerinde anında lifting etkisi',
        'CE & FDA sertifikalı, vücutla %100 biyouyumlu saf hyaluronik asit'
      ]
    },
    {
      id: 'mesotherapy',
      title: 'Gençlik Aşısı & Mezoterapi',
      tagline: 'Paris Işıltısı, Somon DNA, Kolajen Aşısı & Nem İksirleri',
      duration: '30-40 Dk',
      benefit: 'Cildin alt katmanlarına vitamin, aminoasit ve kolajen yüklemesiyle doğal ışıltı',
      icon: Zap,
      targetLayer: 'Papiller & Retiküler Dermis',
      longevity: '6 - 9 Ay Hücresel Canlılık',
      recoveryTime: 'Birkaç Saat İçinde Normale Dönüş',
      doctorLevel: 'Uzman Hekim Protokolü',
      stages: [
        { step: '01', name: 'Deri Kalite Tespiti', desc: 'Nem kaybı, matlık ve elastikiyet zayıflığının dijital analizi' },
        { step: '02', name: 'Kokteyl Formülasyonu', desc: 'NCTF 135HA, Polinükleotid Somon DNA ve vitamin kompleksi seçimi' },
        { step: '03', name: 'Mikro Papül Enjeksiyonu', desc: 'Cildin 1-2 mm altına mezoterapi tabancası / mikro iğnelerle iletim' },
        { step: '04', name: 'Hücresel Kolajen Tetikleme', desc: '3-7 gün içinde başlayan parlaklık, gözenek sıkılaşması ve esneklik' }
      ],
      highlights: [
        'Paris Işıltısı (NCTF) ile hücresel yenilenme ve canlı cilt tonu',
        'Somon DNA aşısı ile elastikiyet kaybı ve yorgun cilt görünümüne son',
        'Kolajen aşısı ile doku sıkılaşması ve ince kırışıklık onarımı',
        'Göz altı morlukları ve ışık aşısı protokolleri'
      ]
    },
    {
      id: 'spot-treatment',
      title: 'Leke Tedavisi & Karbon Peeling',
      tagline: 'Güneş Lekeleri, Melazma, Akne İzi & Gözenek Sıkılaştırma',
      duration: '40 Dk',
      benefit: 'Cilt tonunu eşitleyen, lekeleri açan ve pürüzsüz doku kazandıran lazer protokolü',
      icon: Sparkles,
      targetLayer: 'Melanin Pigment Odakları & Gözenek Duvarı',
      longevity: 'Kalıcı Leke Açılması (Güneş Koruması ile)',
      recoveryTime: 'Sıfır Soyulma & Sıfır Kabuk',
      doctorLevel: 'Medikal Lazer Uzmanı Kontrolünde',
      stages: [
        { step: '01', name: 'Leke Derinliği Analizi', desc: 'Epidermal veya dermal pigmentasyon haritalaması' },
        { step: '02', name: 'Medikal Karbon Maskesi', desc: 'Gözenek içlerine ve leke odaklarına nüfuz eden nano karbon solüsyonu' },
        { step: '03', name: 'Q-Switched Lazer Patlatma', desc: 'Karbon partikülleriyle birlikte ölü hücrelerin ve pigmentlerin buharlaşması' },
        { step: '04', name: 'Yatıştırıcı C Vitamin Kürü', desc: 'Antioksidan koruma ve anında porselen cilt parlaklığı' }
      ],
      highlights: [
        'Medikal karbon solüsyonu ile nano-düzeyde gözenek arındırma ve küçültme',
        'Güneş, doğum ve yaşlılık lekelerine karşı renk açıcı medikal protokoller',
        'Kolajen sentezini tetikleyerek ince çizgi ve akne izi görünümünü azaltma',
        'Soyulma ve kızarıklık olmadan sosyal hayata anında dönüş konforu'
      ]
    },
    {
      id: 'prp-skin',
      title: 'PRP & Cilt Yenileme Tedavisi',
      tagline: 'Kendi Plazmanızla Hücresel Onarım, Kolajen & Doku Canlanması',
      duration: '35-45 Dk',
      benefit: '%100 doğal, alerji riski olmayan güçlü kök hücre & büyüme faktörü desteği',
      icon: ShieldCheck,
      targetLayer: 'Dermal Matriks & Hücre Çekirdeği',
      longevity: 'Uzun Süreli Biyolojik Gençleşme',
      recoveryTime: '1 Gün İçinde Tam İyileşme',
      doctorLevel: 'Yalnızca Uzman Hekim Tarafından',
      stages: [
        { step: '01', name: 'Steril Kan Alımı', desc: 'Özel antikoagülanlı tüplere 8-10 cc kan örneği alınması' },
        { step: '02', name: 'Santrifüj ile Plazma Ayrımı', desc: 'Trombositten ve büyüme faktöründen en zengin sarı plazmanın elde edilmesi' },
        { step: '03', name: 'Dermise Geri İletim', desc: 'Hasarlı, kırışık ve mat bölgelere mikro iğneleme ile zerk edilmesi' },
        { step: '04', name: 'Doğal Doku İyileşmesi', desc: 'Vücudun kendi tamir mekanizmasının kolajen üretimini başlatması' }
      ],
      highlights: [
        'Kişinin kendi kanından ayrıştırılan zengin trombosit plazması (PRP)',
        'Cilt kalitesini artıran, sarkmaları toparlayan yoğun büyüme faktörleri',
        'Göz çevresi aydınlatma, ince kırışıklık ve leke onarımı',
        'Hekim kontrolünde tamamen steril ve kapalı sistem tüp uygulaması'
      ]
    },
  ];

  const currentServiceData = aestheticServices.find((s) => s.id === selectedService) || aestheticServices[0];

  const apollonFaqs = [
    {
      id: 'ap-1',
      question: 'Apollon Güzellik Merkezi hangi hizmetleri sunmaktadır?',
      answer: 'Apollon Güzellik & Medikal Estetik Merkezimizde hekim denetimli Hydrafacial medikal cilt bakımı, botoks, dudak ve yüz dolgusu, gençlik aşısı & mezoterapi, leke tedavisi, karbon peeling, PRP ve kadın/erkek anatomisine özel ayrılmış yeni nesil safir buz lazer epilasyon klinikleri sunulmaktadır.',
      category: 'genel' as const,
    },
    {
      id: 'ap-2',
      question: 'Bursa Kadın Lazer ve Bursa Erkek Lazer klinikleriniz nerede bulunuyor?',
      answer: 'Her iki lazer kliniğimiz de Bursa Nilüfer FSM Bulvarı’nda (BKM yanı) aynı seçkin lokasyonda, kadın ve erkek danışanlarımız için tamamen ayrılmış özel steril odalarda ve %100 mahremiyet ilkeleriyle hizmet vermektedir.',
      category: 'klinik' as const,
    },
    {
      id: 'ap-3',
      question: 'Botoks ve dolgu uygulamaları hekim tarafından mı yapılıyor?',
      answer: 'Evet, tüm botoks, hyaluronik asit dolgu, gençlik aşısı ve mezoterapi uygulamalarımız Sağlık Bakanlığı mevzuatlarına uygun olarak uzman hekimimiz tarafından gerçekleştirilmektedir. Kullanılan tüm ürünler orijinal, CE & FDA onaylıdır.',
      category: 'islem' as const,
    },
    {
      id: 'ap-4',
      question: 'Hydrafacial cilt bakımı kimlere uygulanabilir?',
      answer: 'Hydrafacial medikal cilt bakımı kadın ve erkek tüm danışanlarımıza, her mevsimde güvenle uygulanabilir. Cildin derinlemesine temizlenmesi, siyah noktalardan arınması ve hyaluronik asit ile beslenmesi için en konforlu medikal protokoldür.',
      category: 'islem' as const,
    },
    {
      id: 'ap-5',
      question: 'İlk seanstan önce ücretsiz cilt ve estetik analizi yapıyor musunuz?',
      answer: 'Evet! Kliniğimize geldiğinizde uzman hekim ve medikal estetisyenlerimiz tarafından gelişmiş analiz cihazlarıyla cilt tipi, leke derinliği veya kırışıklık derecesi ücretsiz olarak değerlendirilir ve size en uygun kişiye özel protokol belirlenir.',
      category: 'fiyat' as const,
    },
  ];

  return (
    <div className="space-y-16 sm:space-y-24">
      {/* 1. HERO SECTION: Luxury Medical Aesthetics Reception Hub */}
      <section className="relative overflow-hidden pt-8 pb-16 lg:pt-14 lg:pb-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 text-white">
        {/* Ambient Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent blur-3xl pointer-events-none" />
        <div className="absolute -top-24 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold tracking-wide">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span>Bursa Nilüfer FSM Bulvarı • Hekim Denetimli Medikal Estetik</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight text-white leading-tight">
                Apollon Güzellik & <br className="hidden sm:inline" />
                <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-500 bg-clip-text text-transparent">
                  Medikal Estetik Merkezi
                </span>
              </h1>

              <p className="text-slate-300 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto lg:mx-0 leading-relaxed font-normal">
                Bursa FSM Bulvarı’nda hekim güvencesiyle medikal cilt bakımı, Hydrafacial, botoks, dudak & yüz dolgusu, gençlik aşısı ve kadın-erkek ayrılmış safir buz lazer epilasyon klinikleri.
              </p>

              {/* Badges / Stats Bar */}
              <div className="grid grid-cols-3 gap-3 pt-2 max-w-lg mx-auto lg:mx-0">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-xs">
                  <div className="text-lg sm:text-2xl font-black text-amber-400">12+ Yıl</div>
                  <div className="text-[11px] text-slate-400 font-medium">Klinik Tecrübesi</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-xs">
                  <div className="text-lg sm:text-2xl font-black text-amber-400">%100</div>
                  <div className="text-[11px] text-slate-400 font-medium">Hekim Güvencesi</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-xs">
                  <div className="text-lg sm:text-2xl font-black text-amber-400">15.000+</div>
                  <div className="text-[11px] text-slate-400 font-medium">Mutlu Danışan</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 pt-4">
                <button
                  onClick={() => onOpenLeadModal('Ücretsiz Medikal Cilt & Estetik Analizi Randevusu')}
                  className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Ücretsiz Analiz & Randevu Al</span>
                </button>

                <a
                  href={`https://wa.me/${siteProfile.whatsapp || '905312392892'}?text=${encodeURIComponent(siteProfile.whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-400" />
                  <span>WhatsApp Canlı Danışma</span>
                </a>
              </div>
            </div>

            {/* Right: High-Converting Medical Reception Hub (No broken images, pure SEO-rich UI) */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="absolute -inset-1.5 bg-gradient-to-r from-amber-500/25 via-amber-400/15 to-rose-500/25 rounded-3xl blur-xl" />
                
                <div className="relative rounded-3xl border border-amber-500/30 bg-gradient-to-b from-slate-900/95 via-slate-950 to-slate-900 shadow-2xl p-6 sm:p-7 backdrop-blur-xl space-y-5">
                  
                  {/* Top Live Status Bar */}
                  <div className="flex items-center justify-between pb-4 border-b border-white/10">
                    <div className="flex items-center gap-2.5">
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                      </span>
                      <span className="text-xs font-bold text-slate-200">FSM Klinik Aktif Danışma</span>
                    </div>
                    <span className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold">
                      Nilüfer • BKM Yanı
                    </span>
                  </div>

                  {/* 4 Specialized Medical Pillars */}
                  <div className="space-y-2.5">
                    
                    {/* Pillar 1: Botoks */}
                    <div className="p-3.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs sm:text-sm font-bold text-white">Medikal Botoks & Kırışıklık</h4>
                          <span className="text-[10px] text-amber-300 font-medium">Hekim Uygulaması</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                          Alın, kaş arası, göz çevresi & masseter diş sıkma botoksu.
                        </p>
                      </div>
                    </div>

                    {/* Pillar 2: Dolgu */}
                    <div className="p-3.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-rose-500/20 border border-rose-500/40 text-rose-300 flex items-center justify-center shrink-0 mt-0.5">
                        <Smile className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs sm:text-sm font-bold text-white">Dudak & Yüz Dolgusu</h4>
                          <span className="text-[10px] text-rose-300 font-medium">FDA Onaylı</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                          Russian lips, Jawline çene hattı, nazolabial ve elmacık hacmi.
                        </p>
                      </div>
                    </div>

                    {/* Pillar 3: Cilt Bakımı */}
                    <div className="p-3.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-sky-500/20 border border-sky-500/40 text-sky-300 flex items-center justify-center shrink-0 mt-0.5">
                        <Droplets className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs sm:text-sm font-bold text-white">Hydrafacial Cilt Bakımı</h4>
                          <span className="text-[10px] text-sky-300 font-medium">Derin Temizlik</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                          Vakumlu vorteks gözenek arındırma ve hyaluronik asit nem kürü.
                        </p>
                      </div>
                    </div>

                    {/* Pillar 4: Lazer Epilasyon */}
                    <div className="p-3.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center shrink-0 mt-0.5">
                        <Zap className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs sm:text-sm font-bold text-white">Kadın & Erkek Buz Lazer</h4>
                          <span className="text-[10px] text-emerald-300 font-medium">-5°C Safir Başlık</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                          Ayrılmış özel seans odaları ve hekim gözetiminde kesin sonuç.
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* Trust Micro-Footer */}
                  <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-400">
                    <span className="flex items-center gap-1.5 text-slate-300 font-medium">
                      <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                      Karekodlu Orijinal Ürünler
                    </span>
                    <span className="text-amber-400 font-semibold">
                      %100 Hijyen Standardı
                    </span>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. DUAL LASER CLINIC GATEWAYS (Bursa Kadın Lazer & Bursa Erkek Lazer) */}
      <section id="lazer-klinikleri" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-2.5 mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold">
            <Compass className="w-3.5 h-3.5 text-amber-600" />
            <span>Özelleştirilmiş Lazer Klinikleri</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            Kadın ve Erkek İçin <span className="text-amber-600">Ayrılmış Lazer Merkezleri</span>
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            Farklı kıl anatomisi, hormon yapısı ve mahremiyet beklentilerine özel olarak yapılandırılmış iki bağımsız uzmanlık kliniğimiz:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          
          {/* Card 1: Kadın Lazer Kliniği (Professional Medical Data Panel, No Image Placeholders) */}
          <div className="group relative rounded-3xl overflow-hidden bg-gradient-to-br from-rose-950 via-slate-950 to-slate-900 text-white p-7 sm:p-9 border border-rose-500/30 shadow-xl transition-all duration-300 hover:shadow-2xl hover:border-rose-400/60 hover:-translate-y-1 flex flex-col justify-between">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200 text-xs font-bold">
                  <Heart className="w-3 h-3 text-rose-300" />
                  Kadın Lazer Kliniği
                </span>
                <span className="text-xs text-rose-200/80 font-mono">bursakadinlazer.com.tr</span>
              </div>

              {/* Scientific / Clinical Feature Grid (Replaces Static Image) */}
              <div className="grid grid-cols-2 gap-2.5 p-4 rounded-2xl bg-rose-950/50 border border-rose-500/20 backdrop-blur-xs">
                <div className="space-y-1">
                  <div className="text-[10px] text-rose-300 uppercase tracking-wider font-bold">Uygulayıcı Kadro</div>
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-rose-400" />
                    Kadın Estetisyenler
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] text-rose-300 uppercase tracking-wider font-bold">Soğutma Gücü</div>
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-sky-400" />
                    -5°C Safir Başlık
                  </div>
                </div>
                <div className="space-y-1 pt-2 border-t border-rose-500/20">
                  <div className="text-[10px] text-rose-300 uppercase tracking-wider font-bold">Özel Tedavi</div>
                  <div className="text-xs font-bold text-white">Çilek Bacak & Batık</div>
                </div>
                <div className="space-y-1 pt-2 border-t border-rose-500/20">
                  <div className="text-[10px] text-rose-300 uppercase tracking-wider font-bold">Seans Süresi</div>
                  <div className="text-xs font-bold text-white">Tüm Vücut 35 Dk</div>
                </div>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Bursa Kadın Lazer Epilasyon
              </h3>

              <p className="text-rose-100/80 text-xs sm:text-sm leading-relaxed">
                Kadın estetisyenler eşliğinde %100 mahremiyet. Bacak, koltuk altı, genital ve yüz bölgesinde batık ve çilek bacak sorunlarına son veren safir buz lazer konforu.
              </p>

              <ul className="space-y-2 pt-1 text-xs text-rose-100">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>Kadın Estetisyenler & Özel Steril Odalar</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>Dört Mevsim Uygulanabilir Güvenli Teknoloji</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>Batık, Kararma ve Çilek Bacak Protokolleri</span>
                </li>
              </ul>
            </div>

            <div className="pt-7">
              <button
                onClick={() => {
                  if (typeof window !== 'undefined' && isProductionDomain(window.location.hostname)) {
                    window.location.href = 'https://bursakadinlazer.com.tr';
                  } else {
                    setSiteKey('kadin');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="w-full py-3.5 px-5 rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-rose-950/60 cursor-pointer active:scale-98"
              >
                <span>Kadın Lazer Sitesini İncele</span>
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Card 2: Erkek Lazer Kliniği (Professional Medical Data Panel, No Image Placeholders) */}
          <div className="group relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white p-7 sm:p-9 border border-amber-500/30 shadow-xl transition-all duration-300 hover:shadow-2xl hover:border-amber-400/60 hover:-translate-y-1 flex flex-col justify-between">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs font-bold">
                  <ShieldCheck className="w-3 h-3 text-amber-400" />
                  Erkek Lazer Kliniği
                </span>
                <span className="text-xs text-amber-200/80 font-mono">bursaerkeklazer.com.tr</span>
              </div>

              {/* Scientific / Clinical Feature Grid (Replaces Static Image) */}
              <div className="grid grid-cols-2 gap-2.5 p-4 rounded-2xl bg-amber-950/30 border border-amber-500/20 backdrop-blur-xs">
                <div className="space-y-1">
                  <div className="text-[10px] text-amber-300 uppercase tracking-wider font-bold">Kıl Anatomisi</div>
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5 text-amber-400" />
                    Kalın & Derin Kıl Kökü
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] text-amber-300 uppercase tracking-wider font-bold">Soğutma Gücü</div>
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-sky-400" />
                    Safir Soğutmalı Başlık
                  </div>
                </div>
                <div className="space-y-1 pt-2 border-t border-amber-500/20">
                  <div className="text-[10px] text-amber-300 uppercase tracking-wider font-bold">Popüler Bölgeler</div>
                  <div className="text-xs font-bold text-white">Sakal Üstü, Ense, Sırt</div>
                </div>
                <div className="space-y-1 pt-2 border-t border-amber-500/20">
                  <div className="text-[10px] text-amber-300 uppercase tracking-wider font-bold">Mahremiyet</div>
                  <div className="text-xs font-bold text-white">%100 Özel Oda</div>
                </div>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Bursa Erkek Lazer Epilasyon
              </h3>

              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                Erkek derin kıl kökü ve kalın kıl anatomisine özel dalga boyları. Sakal üstü, ense, sırt, göğüs, omuz ve özel bölgede kalıcı pürüzsüzlük ve sıfır tahriş.
              </p>

              <ul className="space-y-2 pt-1 text-xs text-slate-200">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Erkek Anatomisine Özel Yüksek Joule Buz Lazer</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Sakal Şekillendirme & Batık / Kıl Dönmesi Çözümü</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>%100 Erkek Mahremiyeti ve Hekim Güvencesi</span>
                </li>
              </ul>
            </div>

            <div className="pt-7">
              <button
                onClick={() => {
                  if (typeof window !== 'undefined' && isProductionDomain(window.location.hostname)) {
                    window.location.href = 'https://bursaerkeklazer.com.tr';
                  } else {
                    setSiteKey('erkek');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="w-full py-3.5 px-5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-950/60 cursor-pointer active:scale-98"
              >
                <span>Erkek Lazer Sitesini İncele</span>
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* 3. AESTHETIC & SKINCARE SERVICES CATALOG */}
      <section id="hizmetler" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-2.5 mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
            <span>Apollon Medikal Estetik & Cilt Bakımı</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            Botoks, Dolgu & <span className="text-amber-600">Medikal Cilt Protokolleri</span>
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            Hekim denetiminde, Sağlık Bakanlığı onaylı medikal ürünler ve uzman estetisyen dokunuşlarıyla cildinize hak ettiği gençliği ve ışıltıyı kazandırın.
          </p>
        </div>

        {/* Services Tabs / Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left: Service Selector */}
          <div className="lg:col-span-5 space-y-3">
            {aestheticServices.map((service) => {
              const Icon = service.icon;
              const isSelected = selectedService === service.id;
              return (
                <button
                  key={service.id}
                  onClick={() => setSelectedService(service.id)}
                  className={`w-full text-left p-4 sm:p-5 rounded-2xl border transition-all flex items-start gap-4 cursor-pointer ${
                    isSelected
                      ? 'bg-slate-900 text-white border-amber-500/60 shadow-lg shadow-amber-900/10'
                      : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-3 rounded-xl shrink-0 ${
                    isSelected ? 'bg-amber-500 text-slate-950' : 'bg-slate-100 text-slate-700'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="font-bold text-sm sm:text-base leading-snug">
                      {service.title}
                    </div>
                    <div className={`text-xs truncate ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                      {service.tagline}
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 shrink-0 self-center transition-transform ${
                    isSelected ? 'text-amber-400 translate-x-1' : 'text-slate-400'
                  }`} />
                </button>
              );
            })}
          </div>

          {/* Right: Selected Service Detailed Card (Pure Scientific Clinical Layout, No Static SVG Image) */}
          <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm p-6 sm:p-8 space-y-6">
            
            {/* Header & Clinical Meta Panel */}
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white p-6 border border-slate-800 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold">
                  <Stethoscope className="w-3.5 h-3.5 text-amber-400" />
                  {currentServiceData.doctorLevel}
                </span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 text-slate-200">
                  İşlem Süresi: {currentServiceData.duration}
                </span>
              </div>

              <div className="space-y-1.5">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  {currentServiceData.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  {currentServiceData.tagline}
                </p>
              </div>

              {/* 3 Medical Parameters */}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
                <div className="bg-white/5 rounded-xl p-2.5 text-center">
                  <div className="text-[10px] text-slate-400 font-medium">Hedef Doku</div>
                  <div className="text-xs font-bold text-amber-300 truncate">{currentServiceData.targetLayer}</div>
                </div>
                <div className="bg-white/5 rounded-xl p-2.5 text-center">
                  <div className="text-[10px] text-slate-400 font-medium">Etki Süresi</div>
                  <div className="text-xs font-bold text-amber-300 truncate">{currentServiceData.longevity}</div>
                </div>
                <div className="bg-white/5 rounded-xl p-2.5 text-center">
                  <div className="text-[10px] text-slate-400 font-medium">İyileşme</div>
                  <div className="text-xs font-bold text-amber-300 truncate">{currentServiceData.recoveryTime}</div>
                </div>
              </div>
            </div>

            {/* Benefit Box */}
            <div className="p-4 rounded-xl bg-amber-50/90 border border-amber-200 text-amber-950 text-xs sm:text-sm font-medium flex items-start gap-2.5">
              <Award className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold text-amber-900">Temel Klinik Kazanım:</strong> {currentServiceData.benefit}
              </div>
            </div>

            {/* 4-Step Protocol Flow */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <Activity className="w-4 h-4 text-amber-600" />
                Uygulama Aşamaları & Klinik Protokol:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {currentServiceData.stages.map((st) => (
                  <div key={st.step} className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-md bg-slate-900 text-amber-400 font-black text-[10px] flex items-center justify-center shrink-0">
                        {st.step}
                      </span>
                      <span className="font-bold text-xs text-slate-900">{st.name}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 leading-snug pl-7">
                      {st.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights Checkmarks */}
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Öne Çıkan Avantajlar:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {currentServiceData.highlights.map((hl, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-700 bg-slate-50/50 p-2 rounded-lg">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{hl}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={() => onOpenLeadModal(`${currentServiceData.title} Bilgi & Randevu`)}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md"
              >
                <Calendar className="w-4 h-4 text-amber-400" />
                <span>Bu İşlem İçin Randevu Al</span>
              </button>
              <a
                href={`https://wa.me/905312392892?text=${encodeURIComponent(`Merhaba, Apollon Güzellik ${currentServiceData.title} hakkında detaylı bilgi ve fiyat almak istiyorum.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-5 py-3.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                <span>WhatsApp ile Hekime Danış</span>
              </a>
            </div>

          </div>

        </div>
      </section>

      {/* 4. DOCTOR & CLINICAL TRUST SECTION */}
      <section className="bg-slate-900 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>Klinik Standartları & Hekim Güvencesi</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                Neden Bursa’da <span className="text-amber-400">Apollon Medikal Estetik?</span>
              </h2>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Estetik, botoks, dolgu ve lazer epilasyon uygulamalarında en yüksek hijyen standartlarını, hekim gözetimini ve kişiye özel medikal protokolleri bir araya getiriyoruz.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 mt-1">
                    <UserCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white">Uzman Hekim ve Deneyimli Medikal Kadro</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">Tüm enjeksiyonlu işlemler (botoks, dolgu, gençlik aşısı) doğrudan uzman hekim tarafından yapılır.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 mt-1">
                    <Award className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white">FDA Onaylı Orijinal Medikal Ürün Parkuru</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">Kliniğimizde sadece CE ve FDA sertifikalı, barkodlu orijinal dolgu, botoks ve cilt bakım serumları kullanılır.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 mt-1">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white">%100 Sterilizasyon ve Ayrı Klinik Odaları</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">Kadın ve erkek danışanlarımız için ayrılmış özel seans odaları ve medikal hijyen standartları.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              <div className="bg-slate-800/80 border border-white/10 rounded-2xl p-6 text-center space-y-2">
                <div className="text-3xl sm:text-4xl font-black text-amber-400">%99.2</div>
                <div className="text-xs font-bold text-slate-200">Hasta & Danışan Memnuniyeti</div>
                <div className="text-[11px] text-slate-400">Doğal Sonuçlar & Yüksek Güven</div>
              </div>
              <div className="bg-slate-800/80 border border-white/10 rounded-2xl p-6 text-center space-y-2">
                <div className="text-3xl sm:text-4xl font-black text-amber-400">15.000+</div>
                <div className="text-xs font-bold text-slate-200">Başarılı Uygulama</div>
                <div className="text-[11px] text-slate-400">Bursa Nilüfer FSM Bulvarı</div>
              </div>
              <div className="bg-slate-800/80 border border-white/10 rounded-2xl p-6 text-center space-y-2">
                <div className="text-3xl sm:text-4xl font-black text-amber-400">FDA / CE</div>
                <div className="text-xs font-bold text-slate-200">Sertifikalı Ürünler</div>
                <div className="text-[11px] text-slate-400">Karekodlu Orijinal Medikal İlaçlar</div>
              </div>
              <div className="bg-slate-800/80 border border-white/10 rounded-2xl p-6 text-center space-y-2">
                <div className="text-3xl sm:text-4xl font-black text-amber-400">7/24</div>
                <div className="text-xs font-bold text-slate-200">WhatsApp Destek</div>
                <div className="text-[11px] text-slate-400">İşlem Sonrası Sürekli Takip</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. APOLLON BLOG & KNOWLEDGE CENTER */}
      <section id="blog-section">
        <BlogSection
          posts={posts}
          isLoading={isBlogsLoading}
          onNavigateToPost={onNavigateToPost}
        />
      </section>

      {/* 6. FAQ & LOCATION */}
      <section id="sss">
        <FaqSection customFaqs={apollonFaqs} />
      </section>

      <section id="iletisim">
        <LocationSection onOpenLeadModal={onOpenLeadModal} />
      </section>
    </div>
  );
};

